import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Radio, ChevronDown, ChevronUp, Clock, Users, CheckCircle2, ListChecks, FileText, Plus, Check, Loader2 } from "lucide-react";

const fmtDuration = (s) => {
  const n = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const ss = n % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
};

function Avatar({ person }) {
  const name = person?.name || person?.email || "?";
  const initials = name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const url = person?.avatar || person?.avatar_url || person?.photo_url;
  if (url) {
    return <img src={url} alt={name} className="w-5 h-5 rounded-full object-cover ring-1 ring-hairline" />;
  }
  return (
    <span className="w-5 h-5 rounded-full bg-brand/25 text-brand text-[9px] font-bold flex items-center justify-center">
      {initials || "?"}
    </span>
  );
}

/**
 * MeetingSummaryCard — renders a `meeting_summary` message posted by the
 * Meeting Recorder. Shows title, duration, participants, TL;DR, decisions and
 * action items. Collapsible; display-only (action-items→tasks lands in M3).
 */
export default function MeetingSummaryCard({ message }) {
  const md = message?.metadata || {};
  const [open, setOpen] = useState(true);
  const [added, setAdded] = useState({});
  const [addingAll, setAddingAll] = useState(false);
  const [members, setMembers] = useState([]);
  const [pickerFor, setPickerFor] = useState(null);
  const [assignees, setAssignees] = useState({});
  const decisions = Array.isArray(md.decisions) ? md.decisions : [];
  const actionItems = Array.isArray(md.action_items) ? md.action_items : [];
  const participants = Array.isArray(md.participants) ? md.participants.filter(Boolean) : [];
  const canCreateTasks = md.chat_kind === "project" || md.chat_kind === "development";

  useEffect(() => {
    if (!canCreateTasks || !md.destination_chat_id) return;
    let cancelled = false;
    (async () => {
      try {
        const [chatRes, taskRes] = await Promise.all([
          api.get(`/chats/${md.destination_chat_id}`),
          api.get("/tasks", { params: { source_chat_id: md.destination_chat_id, source_message_id: message.id, status_filter: "active" } }),
        ]);
        if (cancelled) return;
        const mem = (chatRes.data.members || []).filter((m) => !String(m.id).startsWith("ai-"));
        setMembers(mem);
        const memById = Object.fromEntries(mem.map((m) => [m.id, m]));
        const byTitle = {};
        (taskRes.data || []).forEach((t) => { byTitle[(t.title || "").trim()] = t; });
        const addedMap = {}; const assigneeMap = {};
        actionItems.forEach((a, i) => {
          const t = byTitle[(a || "").slice(0, 200).trim()];
          if (t) { addedMap[i] = "done"; if (t.assigned_to) assigneeMap[i] = memById[t.assigned_to] || { id: t.assigned_to, name: "Member" }; }
        });
        setAdded((s) => ({ ...addedMap, ...s }));
        setAssignees((s) => ({ ...assigneeMap, ...s }));
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureMembers = async () => {
    if (members.length || !md.destination_chat_id) return;
    try {
      const { data } = await api.get(`/chats/${md.destination_chat_id}`);
      setMembers((data.members || []).filter((m) => !String(m.id).startsWith("ai-")));
    } catch { /* keep empty — falls back to unassigned */ }
  };

  const createTask = async (i, title, assignedTo = null) => {
    if (added[i] || !md.destination_chat_id) return false;
    setAdded((s) => ({ ...s, [i]: "adding" }));
    try {
      await api.post("/tasks", {
        title: (title || "Follow-up from meeting").slice(0, 200),
        source_chat_id: md.destination_chat_id,
        source_message_id: message.id,
        assigned_to: assignedTo || null,
        status: "todo",
      });
      setAdded((s) => ({ ...s, [i]: "done" }));
      if (assignedTo) {
        const m = members.find((x) => x.id === assignedTo);
        if (m) setAssignees((s) => ({ ...s, [i]: m }));
      }
      return true;
    } catch (e) {
      setAdded((s) => { const n = { ...s }; delete n[i]; return n; });
      toast.error(e?.response?.data?.detail?.message || "Could not create task");
      return false;
    }
  };

  const addAll = async () => {
    setAddingAll(true);
    let n = 0;
    for (let i = 0; i < actionItems.length; i++) {
      if (added[i]) continue;
      // eslint-disable-next-line no-await-in-loop
      if (await createTask(i, actionItems[i])) n += 1;
    }
    setAddingAll(false);
    if (n > 0) toast.success(`Added ${n} task${n === 1 ? "" : "s"} to this project`);
  };

  return (
    <div className="w-full flex justify-center my-3 px-2 md:px-4" data-testid={`meeting-summary-${message.id}`}>
      <div className="w-full max-w-[560px] rounded-2xl border border-brand/30 bg-surface-2/70 overflow-hidden">
        <button
          type="button"
          data-testid={`meeting-summary-toggle-${message.id}`}
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
        >
          <div className="w-9 h-9 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
            <Radio className="w-4 h-4 text-brand" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-bold text-ink truncate">{md.title || "Meeting summary"}</div>
            <div className="flex items-center gap-3 mt-0.5 text-[11px] text-ink-mute">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {fmtDuration(md.duration_seconds)}
              </span>
              {(md.participant_count || participants.length) > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3 h-3" /> {md.participant_count || participants.length}
                </span>
              )}
              {actionItems.length > 0 && (
                <span className="inline-flex items-center gap-1 text-brand">
                  <ListChecks className="w-3 h-3" /> {actionItems.length} action{actionItems.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-ink-mute shrink-0" /> : <ChevronDown className="w-4 h-4 text-ink-mute shrink-0" />}
        </button>

        {open && (
          <div className="px-4 pb-4 space-y-3.5">
            {md.tldr ? (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-mute mb-1">TL;DR</div>
                <p className="text-[13px] leading-relaxed text-ink-dim">{md.tldr}</p>
              </div>
            ) : null}

            {decisions.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-mute mb-1.5">Decisions</div>
                <ul className="space-y-1.5">
                  {decisions.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-ink-dim" data-testid={`meeting-decision-${message.id}-${i}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 text-tn-green mt-0.5 shrink-0" />
                      <span className="leading-snug">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {actionItems.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-ink-mute">Action items</div>
                  {canCreateTasks && (
                    <button
                      type="button"
                      data-testid={`meeting-add-all-tasks-${message.id}`}
                      onClick={addAll}
                      disabled={addingAll}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:opacity-80 disabled:opacity-50"
                    >
                      {addingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Add all to tasks
                    </button>
                  )}
                </div>
                <ul className="space-y-1.5">
                  {actionItems.map((a, i) => {
                    const state = added[i];
                    return (
                      <li key={i} className="flex items-start gap-2 text-[13px] text-ink-dim" data-testid={`meeting-action-${message.id}-${i}`}>
                        <ListChecks className="w-3.5 h-3.5 text-brand mt-0.5 shrink-0" />
                        <span className="leading-snug flex-1">{a}</span>
                        {assignees[i] && (
                          <span className="shrink-0 flex items-center gap-1" data-testid={`meeting-assignee-${message.id}-${i}`} title={assignees[i].name}>
                            <Avatar person={assignees[i]} />
                          </span>
                        )}
                        {canCreateTasks && (
                          <div className="relative shrink-0">
                            <button
                              type="button"
                              data-testid={`meeting-action-task-${message.id}-${i}`}
                              onClick={() => { if (state) return; ensureMembers(); setPickerFor(pickerFor === i ? null : i); }}
                              disabled={state === "adding"}
                              className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-semibold ring-1 transition-colors ${
                                state === "done" ? "text-tn-green ring-tn-green/40" : "text-brand ring-brand/30 hover:bg-brand/10"
                              }`}
                            >
                              {state === "done" ? (
                                <><Check className="w-3 h-3" /> Added</>
                              ) : state === "adding" ? (
                                <><Loader2 className="w-3 h-3 animate-spin" /> …</>
                              ) : (
                                <><Plus className="w-3 h-3" /> Task</>
                              )}
                            </button>
                            {pickerFor === i && !state && (
                              <div className="absolute right-0 top-7 z-20 w-48 max-h-56 overflow-y-auto rounded-xl bg-surface2 ring-1 ring-hairline shadow-xl py-1" data-testid={`meeting-assign-menu-${message.id}-${i}`}>
                                <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-ink-mute">Assign to</div>
                                <button
                                  type="button"
                                  data-testid={`meeting-assign-none-${message.id}-${i}`}
                                  onClick={() => { setPickerFor(null); createTask(i, a, null); }}
                                  className="w-full text-left px-3 py-1.5 text-[13px] text-ink hover:bg-white/5"
                                >
                                  Unassigned
                                </button>
                                {members.map((m) => (
                                  <button
                                    key={m.id}
                                    type="button"
                                    data-testid={`meeting-assign-${m.id}-${message.id}-${i}`}
                                    onClick={() => { setPickerFor(null); createTask(i, a, m.id); }}
                                    className="w-full text-left px-3 py-1.5 text-[13px] text-ink hover:bg-white/5 truncate"
                                  >
                                    {m.name || m.email || "Member"}
                                  </button>
                                ))}
                                {members.length === 0 && (
                                  <div className="px-3 py-1.5 text-[12px] text-ink-mute">No members found</div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {!canCreateTasks && (
                  <p className="text-[11px] text-ink-mute mt-2" data-testid={`meeting-tasks-hint-${message.id}`}>
                    Turn this into a Project Chat to track these as tasks.
                  </p>
                )}
              </div>
            )}

            {!md.tldr && decisions.length === 0 && actionItems.length === 0 && (
              <p className="text-[13px] text-ink-mute italic flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" />
                {md.has_transcript ? "No highlights were extracted." : "No speech was transcribed for this meeting."}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
